import { Link, useParams, useNavigate } from "react-router";
import { IoArrowBack } from "react-icons/io5";
import { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useProjectsPage, useProjectsStore } from "../state/projects";
import { UserInfo } from "../components/UserInfo";
import { useLocationsPage, useLocationsStore } from "../state/locations";
import { toast } from "react-toastify";
import { RackUnit, MiniRack, MultiViewRack } from "../components/Rack/RackUnit";
import { Rack3D } from "../components/Rack/Rack3D";
import { updateUnit, deleteUnit } from "../api/locations";
import { DesignSystem, getBadgeTailwindClasses } from "../styles/designSystem";
import { FaServer, FaInfoCircle, FaChevronLeft, FaChevronRight, FaEye, FaList, FaCube } from "react-icons/fa";
import { FiEdit3 } from "react-icons/fi";
import { NotificationButton } from "../components/NotificationButton";
import { CreateDropdown } from "../components/CreateDropdown";
import { AddAssetToRackUnitModal } from "../components/Rack/AddAssetToRackUnitModal";
import { AssetDetailsModal } from "../components/Project/AssetDetailsModal";
import { getProjectAssetByUnitId } from "../api/assets";
import { ProjectAsset } from "../api/assets";

export const Rack = () => {
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  const params = useParams<{ projectId: string, siteId: string, floorId: string, roomId: string, rackId: string, rowId: string }>();
  const navigate = useNavigate();
  const { keycloak: { token }, keycloak } = useKeycloak();
  const { loadProjectById, loadProjectsPage } = useProjectsPage();
  const { loadFloors, loadRooms, forceReloadRooms } = useLocationsPage();
  const { floors, roomsByFloor } = useLocationsStore();
  const projectData: ProjectInterface | 'not-found' = useProjectsStore((state) => state.fullProjects.find((p) => p.id === params.projectId) || 'not-found');
  const floor = floors[params.siteId || '']?.find((f:FloorInterface) => f.id === params.floorId);
  const room = roomsByFloor[params.floorId || '']?.find((r:RoomInterface) => r.id === params.roomId);
  const siteId = params.siteId || (projectData !== 'not-found' ? (projectData?.sites?.[0]?.site_id || '') : '');
  const row = room?.rows?.find((r:RowInterface) => r.id === params.rowId);
  const rack = row?.racks?.find((r:RackInterface) => r.id === params.rackId);

  // Drag and drop state
  const [draggedUnit, setDraggedUnit] = useState<{ unit: RackUnitInterface, originalPosition: number } | null>(null);
  
  // View mode state
  const [viewMode, setViewMode] = useState<'front' | 'all' | '3d'>('front');
  const [isViewChanging, setIsViewChanging] = useState(false);

  // Safe view mode change function
  const safeSetViewMode = (newMode: 'front' | 'all' | '3d') => {
    if (isViewChanging || viewMode === newMode) return;
    
    setIsViewChanging(true);
    setViewMode(newMode);
    
    // Reset after a short delay to prevent rapid clicking
    setTimeout(() => {
      setIsViewChanging(false);
    }, 300);
  };
  
  // Clean view state
  const [isCleanView, setIsCleanView] = useState(false);
  
  // Legend visibility state
  const [showLegend, setShowLegend] = useState(true);
  
  // Asset modal state
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedUnitPosition, setSelectedUnitPosition] = useState<number | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  
  // Asset details modal state
  const [showAssetDetailsModal, setShowAssetDetailsModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<ProjectAsset | null>(null);
  
  // Track assets assigned to rack units (in real app, this would come from API)
  const [unitAssets, setUnitAssets] = useState<{ [position: number]: { assetId: string; assetName: string } }>({});

  // Rack carousel logic
  const allRacks = row?.racks || [];
  const currentRackIndex = allRacks.findIndex(r => r.id === params.rackId);
  const prevRack = currentRackIndex > 0 ? allRacks[currentRackIndex - 1] : null;
  const nextRack = currentRackIndex < allRacks.length - 1 ? allRacks[currentRackIndex + 1] : null;

  const navigateToRack = (rackId: string) => {
    navigate(`/projects/${params.projectId}/sites/${params.siteId}/floors/${params.floorId}/rooms/${params.roomId}/rows/${params.rowId}/racks/${rackId}`);
  };

  // Asset management functions
  const handleAddAsset = async (position: number) => {
    let rackUnit = rack?.units?.find((u) => u.position === position);
    
    console.log('🔧 Detailed rack unit lookup:', {
      searchPosition: position,
      found: !!rackUnit,
      rackUnit: rackUnit ? {
        id: rackUnit.id,
        position: rackUnit.position,
        device_model: rackUnit.device_model,
        reserved_units: rackUnit.reserved_units
      } : null,
      allUnits: rack?.units?.map(u => ({
        id: u.id,
        position: u.position,
        device_model: u.device_model,
        reserved_units: u.reserved_units,
        hasValidId: !!u.id,
        positionType: typeof u.position
      })),
      positionType: typeof position,
      strictMatch: rack?.units?.find(u => u.position === position),
      looseMatches: rack?.units?.filter(u => u.position == position), // == instead of ===
      rackId: rack?.id,
      totalUnits: rack?.units?.length
    });
    
    // For empty positions (like freeform), allow adding assets and create rack unit automatically
    if (!rackUnit) {
      console.log('🔧 No rack unit at position, will create one when asset is added');
    setSelectedUnitPosition(position);
      setSelectedUnitId(null); // null = create new rack unit with asset
      setShowAssetModal(true);
      return;
    }
    
    if (!rackUnit.id) {
      console.error('🔧 Rack unit found but missing ID:', rackUnit);
      toast.error(`Rack unit at position ${position} has invalid ID. Please refresh the page.`);
      return;
    }
    
    setSelectedUnitPosition(position);
    setSelectedUnitId(rackUnit.id);
    setShowAssetModal(true);
  };

  const handleAssetAdded = (assetId: string, unitPosition: number) => {
    // Update local state to show asset is assigned to this rack unit
    setUnitAssets(prev => ({
      ...prev,
      [unitPosition]: { assetId, assetName: `Asset ${assetId}` } // In real app, get asset name from API
    }));
    setShowAssetModal(false);
    setSelectedUnitPosition(null);
    setSelectedUnitId(null);
  };

  // Handle viewing asset details
  const handleViewAsset = async (unitId: string) => {
    if (!token || !params.projectId) {
      toast.error('Authentication required to view asset details');
      return;
    }

    try {
      console.log('🔍 Loading asset details for unit:', unitId);
      const asset = await getProjectAssetByUnitId(token, params.projectId, unitId, keycloak);
      
      if (asset) {
        setSelectedAsset(asset);
        setShowAssetDetailsModal(true);
        console.log('✅ Asset loaded:', asset.name);
      } else {
        toast.error('Asset not found for this rack unit');
      }
    } catch (error: any) {
      console.error('❌ Error loading asset details:', error);
      toast.error(`Failed to load asset details: ${error.message}`);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && prevRack) {
        navigateToRack(prevRack.id || '');
      } else if (e.key === 'ArrowRight' && nextRack) {
        navigateToRack(nextRack.id || '');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevRack, nextRack]);

  useEffect(() => {
    if (token && projectData === 'not-found' && !isUnmounted) {
      try {
        loadProjectsPage(1);
        loadProjectById(params.projectId || '');
      } catch(e: Error | any) {
        toast.error('Failed to load project or floor');
      }
    }
  }, [params.projectId, token, isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);

  useEffect(() => {
    if (!token || !params.projectId || isUnmounted) return;

    try {
      if (!room && params.siteId && params.floorId)
        loadRooms(params.projectId, params.siteId, params.floorId);

      if (!floor && siteId)
        loadFloors(params.projectId, siteId);
    } catch(e: Error | any) {
      toast.error('Failed to load data');
    }
  }, [params.siteId, params.floorId, token, siteId, floor, room, isUnmounted]);

  // Drag and drop handlers
  const handleDragStart = (unit: RackUnitInterface, position: number) => {
    console.log('🎯 Drag started:', { unit, position });
    setDraggedUnit({ unit, originalPosition: position });
  };

  const handleDragOver = (position: number) => {
    if (draggedUnit && position !== draggedUnit.originalPosition) {
      console.log('🎯 Drag over position:', position);
    }
  };

  const handleDrop = async (targetPosition: number) => {
    if (!draggedUnit || !rack) {
      setDraggedUnit(null);
      return;
    }

    const { unit, originalPosition } = draggedUnit;
    
    if (targetPosition === originalPosition) {
      setDraggedUnit(null);
      return;
    }

    const targetUnit = rack.units?.find(u => u.position === targetPosition && u.id !== unit.id);
    if (targetUnit) {
      toast.error(`Position ${targetPosition} is already occupied`);
      setDraggedUnit(null);
      return;
    }

    const unitCount = unit.reserved_units || 1;
    if (unitCount > 1) {
      for (let i = 1; i < unitCount; i++) {
        const checkPosition = targetPosition + i;
        if (checkPosition > (rack.total_units || 45)) {
          toast.error(`Not enough space above position ${targetPosition}. Device requires ${unitCount} units.`);
          setDraggedUnit(null);
          return;
        }
        
        const existingUnit = rack.units?.find(u => u.position === checkPosition && u.id !== unit.id);
        if (existingUnit) {
          toast.error(`Position ${checkPosition} is already occupied`);
          setDraggedUnit(null);
          return;
        }
      }
    }

    try {
      console.log('📦 Drag & Drop: Moving unit from position', originalPosition, 'to', targetPosition);
      await updateUnitHandler(unit, { position: targetPosition });
      toast.success(`Device "${unit.device_model}" moved from position ${originalPosition} to ${targetPosition}`);
    } catch (error) {
      console.error('❌ Drag & Drop: Failed to move device:', error);
      toast.error('Failed to move device');
    }

    setDraggedUnit(null);
  };
  
  // addUnitHandler removed - now only assets can be added to empty positions

  const getOccupiedPositions = (excludeUnitId?: string) => {
    const occupied = new Set<number>();
    
    rack?.units?.forEach(unit => {
      if (excludeUnitId && unit.id === excludeUnitId) {
        return;
      }
      
      const reservedUnits = unit.reserved_units || 1;
      if (reservedUnits > 1) {
        for (let i = 1; i < reservedUnits; i++) {
          const occupiedPosition = unit.position + i;
          occupied.add(occupiedPosition);
        }
      }
    });
    
    return { occupied };
  };

  const updateUnitHandler = async (unitData: RackUnitInterface, changes: Partial<RackUnitInterface>) => {
    const newUnitData = {...unitData, ...changes};
    
    console.log('🔄 Updating unit:', unitData.id);
    console.log('📝 Changes:', changes);
    
    if (changes.reserved_units && changes.reserved_units > 1) {
      const newReservedUnits = changes.reserved_units;
      
      for (let i = 1; i < newReservedUnits; i++) {
        const checkPosition = unitData.position + i;
        if (checkPosition >= (rack?.total_units || 45)) {
          toast.error(`Not enough space above. Device requires ${newReservedUnits} units but only ${(rack?.total_units || 45) - unitData.position} positions available.`);
          return;
        }
        
        const existingUnit = rack?.units?.find(u => u.position === checkPosition && u.id !== unitData.id);
        if (existingUnit) {
          toast.error(`Position ${checkPosition} is already occupied by another device.`);
          return;
        }
      }
    }
    
    try {
      await updateUnit(token ?? '', params.projectId || '', unitData.id || '', newUnitData);
      console.log('✅ Unit updated successfully');
      
      await forceReloadRooms(params.projectId || '', params.siteId || '', params.floorId || '');
      toast.success('Unit updated successfully!');
    } catch(e: Error | any) {
      console.error('❌ Error updating unit:', e);
      
      if (e.message?.includes('502')) {
        toast.error('Server temporarily unavailable. Please try again in a few moments.');
      } else if (e.message?.includes('500')) {
        toast.error('Server error occurred. Please check your data and try again.');
      } else if (e.message?.includes('400')) {
        toast.error('Invalid data provided. Please check your inputs.');
      } else if (e.message?.includes('404')) {
        toast.error('Device not found. Please refresh the page and try again.');
      } else if (e.message?.includes('401') || e.message?.includes('403')) {
        toast.error('You do not have permission to update this device.');
      } else {
        toast.error(`Failed to update rack unit: ${e.message}`);
      }
    }
  }

  const deleteUnitHandler = async (unitData: RackUnitInterface) => {
    console.log('🔄 Deleting unit:', unitData.id);
    
    try {
      await deleteUnit(token ?? '', unitData.id || '');
      console.log('✅ Unit deleted successfully');
      
      await forceReloadRooms(params.projectId || '', params.siteId || '', params.floorId || '');
      toast.success('Unit deleted successfully!');
    } catch(e: Error | any) {
      console.error('❌ Error deleting unit:', e);
      
      if (e.message?.includes('502')) {
        toast.error('Server temporarily unavailable. Please try again in a few moments.');
      } else if (e.message?.includes('500')) {
        toast.error('Server error occurred. Please try again.');
      } else if (e.message?.includes('404')) {
        toast.error('Device not found. It may have already been deleted.');
      } else if (e.message?.includes('401') || e.message?.includes('403')) {
        toast.error('You do not have permission to delete this device.');
      } else {
        toast.error(`Failed to delete rack unit: ${e.message}`);
      }
    }
  }

  if (projectData === 'not-found') return <div className="container p-4 mx-auto">Project is loading...</div>;

  return (
    <div className="container p-4 mx-auto">
      {/* Header Navigation - Same as Room */}
      <div className="flex items-center justify-between mb-10">
        <div className="flex gap-2 items-center">
          <Link to="/map"><IoArrowBack /></Link>
          <Link to={`/projects/${params.projectId}`}>{typeof projectData === 'object' && projectData?.name || 'Project'}</Link>
          <span>/</span>
          <Link to={`/projects/${params.projectId}/sites/${params.siteId}/floors/${params.floorId}/rooms/${params.roomId}`}>{room?.name || 'Room'}</Link>
          <span>/</span>
          {rack?.number || 'Rack'}
        </div>
                <div className="flex items-center gap-4">
          <CreateDropdown 
            onSelectProject={() => {}}
            onSelectTeamMember={() => {}}
            onSelectTask={() => {}}
            onSelectActionItem={() => {}}
            onSelectDailyUpdate={() => {}}
          />
          <NotificationButton />
          <UserInfo />
        </div>
      </div>

      <div className="p-2">
        {/* Rack Overview Header - Similar to Room Overview */}
        <div className="mb-6">
          <div className="bg-white rounded-lg transition-shadow duration-200 shadow-sm mb-6" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Header Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Rack Info */}
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <FaServer 
                      className="w-5 h-5"
                      style={{ color: DesignSystem.colors.brand[600] }}
                    />
                    <h2 
                      className="text-xl font-semibold"
                      style={{ 
                        fontFamily: 'Arcadia Display, system-ui, sans-serif',
                        color: DesignSystem.colors.gray[900]
                      }}
                    >
                      {rack?.number || 'Rack'} Overview
                    </h2>
                  </div>
                  
                  {/* Quick Info Badges */}
                  <div className="flex items-center space-x-3">
                    <span 
                      className="px-3 py-1 rounded-md text-xs font-medium"
                      style={{ 
                        backgroundColor: DesignSystem.colors.brand[100],
                        color: DesignSystem.colors.brand[800],
                        fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                      }}
                    >
                      {rack?.total_units || 45}U Rack
                    </span>
                    <span 
                      className="px-3 py-1 rounded-md text-xs font-medium"
                      style={{ 
                        backgroundColor: DesignSystem.colors.success[100],
                        color: DesignSystem.colors.success[800],
                        fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                      }}
                    >
                      {rack?.units?.length || 0} / {rack?.total_units || 45} Occupied
                    </span>
                  </div>
                  {/* Units statistics */}
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-xs font-medium" style={{ color: DesignSystem.colors.gray[700] }}>
                        {rack?.total_units || 45} Units
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-xs font-medium" style={{ color: DesignSystem.colors.gray[700] }}>
                        {Math.max(0, (rack?.total_units || 45) - (rack?.units?.length || 0))} free
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-xs font-medium" style={{ color: DesignSystem.colors.gray[700] }}>
                        {rack?.units?.length || 0} occupied
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* View Toggle Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        safeSetViewMode('front');
                      }}
                      disabled={isViewChanging}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-colors font-medium ${
                        isViewChanging 
                          ? 'opacity-50 cursor-not-allowed' 
                          : viewMode === 'front' 
                          ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={{ 
                        fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                      }}
                    >
                      <FaServer className="w-4 h-4" />
                      <span>Front View</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        safeSetViewMode('all');
                      }}
                      disabled={isViewChanging}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-colors font-medium ${
                        isViewChanging 
                          ? 'opacity-50 cursor-not-allowed' 
                          : viewMode === 'all' 
                          ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={{ 
                        fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                      }}
                    >
                      <FaEye className="w-4 h-4" />
                      <span>All View</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        safeSetViewMode('3d');
                      }}
                      disabled={isViewChanging}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-colors font-medium ${
                        isViewChanging 
                          ? 'opacity-50 cursor-not-allowed' 
                          : viewMode === '3d' 
                            ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                            : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={{ 
                        fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                      }}
                    >
                      <FaCube className="w-4 h-4" />
                      <span>3D View <span className={getBadgeTailwindClasses('dev')}>dev</span></span>
                    </button>
                                         
                  </div>
                  
                  <div className="w-px h-6 bg-gray-200"></div>
                  
                  <button
                    className="flex items-center space-x-2 px-4 py-2 text-sm hover:bg-blue-50 rounded-md transition-colors font-medium"
                    style={{ 
                      color: DesignSystem.colors.brand[600],
                      fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                    }}
                  >
                    <FiEdit3 className="w-4 h-4" />
                    <span>Edit Rack</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Stats Row */}
            <div className="px-6 py-2">
              <div 
                className="flex items-center text-xs font-medium"
                style={{ 
                  color: DesignSystem.colors.primary.textSecondary, 
                  fontFamily: 'Arcadia Text, system-ui, sans-serif' 
                }}
              >
                <div className="flex-1">
                  Rack unit management • Drag & drop devices • Multi-unit support
                </div>
                <div className="flex items-center space-x-4">
                  <span>Serial: {rack?.id?.slice(-8) || 'N/A'}</span>
                  <span>•</span>
                  <span>Max Power: {rack?.max_power || 'N/A'}W</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rack Display */}
        <div className="flex justify-center">
          <div className="w-full max-w-7xl">
            {viewMode === 'all' && rack ? (
              /* All View: Show all three views at once */
              <MultiViewRack
                rack={rack}
                updateUnitHandler={updateUnitHandler}
                deleteUnitHandler={deleteUnitHandler}
                getOccupiedPositions={getOccupiedPositions}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isCleanView={isCleanView}
                onAddAsset={handleAddAsset}
                hasAsset={(position) => !!unitAssets[position]}
                assetName={(position) => unitAssets[position]?.assetName}
                onViewAsset={handleViewAsset}
                projectId={params.projectId}
              />
            ) : viewMode === '3d' && rack ? (
              /* 3D View: Interactive 3D visualization */
              <div className="flex justify-center">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full max-w-5xl">
                  <div className="flex items-center justify-between mb-6">
                    <h2 
                      className="text-lg font-semibold"
                      style={{ 
                        color: DesignSystem.colors.primary.text,
                        fontFamily: 'Arcadia Text, system-ui, sans-serif'
                      }}
                    >
                      3D Rack Visualization <span className={getBadgeTailwindClasses('dev')}>dev</span>
                    </h2>
                    <div className="flex items-center space-x-2">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: DesignSystem.colors.primary.textSecondary,
                          fontFamily: 'Arcadia Text, system-ui, sans-serif'
                        }}
                      >
                        Interactive 3D view
                      </span>
                    </div>
                  </div>
                  <Rack3D 
                    rack={rack} 
                    onUnitClick={(unit: RackUnitInterface | null) => {
                      if (unit && handleViewAsset) {
                        handleViewAsset(unit.id || '');
                      }
                    }}
                  />
                </div>
              </div>
            ) : isCleanView && viewMode === 'front' && rack ? (
                              /* Clean View: Clean gray view for Front View */
              <div className="flex justify-center">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" style={{ width: '460px' }}>
                  <div className="flex items-center justify-between mb-6">
                    <h2 
                      className="text-lg font-semibold"
                      style={{ 
                        color: DesignSystem.colors.gray[900],
                        fontFamily: DesignSystem.typography.body.fontFamily
                      }}
                    >
                      {rack?.number || 'Rack'} - Clean View
                    </h2>
                    <div 
                      className="text-sm px-3 py-1 rounded-full"
                      style={{ 
                        backgroundColor: DesignSystem.colors.gray[100],
                        color: DesignSystem.colors.gray[700]
                      }}
                    >
                      Clean Layout
                    </div>
                  </div>

                  <div className="rack-container">
                    <div className="bg-gray-50 rounded-lg p-4 mb-1 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div 
                          className="text-sm font-medium"
                          style={{ color: DesignSystem.colors.gray[700] }}
                        >
                          {rack?.number || 'Rack'} - Clean Layout
                        </div>
                        <div className="text-xs text-gray-500">
                          {rack?.total_units || 45}U
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="space-y-1">
                        {new Array(rack?.total_units || 45).fill(null).map((_, index) => {
                          const position = (rack?.total_units || 45) - index;
                          const rackUnit = rack?.units?.find((u) => u.position === position);
                          const { occupied } = getOccupiedPositions();
                          const isOccupiedByAbove = occupied.has(position);

                          if (isOccupiedByAbove) {
                            return null;
                          }

                          return (
                            <div
                              key={position}
                              className={`flex items-center justify-between p-2 rounded border ${
                                rackUnit 
                                  ? 'bg-white border-gray-300' 
                                  : 'bg-gray-100 border-gray-200 border-dashed'
                              }`}
                              style={{ 
                                minHeight: '40px',
                                color: DesignSystem.colors.gray[700]
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <div 
                                  className="text-xs font-mono w-8 text-center"
                                  style={{ color: DesignSystem.colors.gray[500] }}
                                >
                                  {String(position).padStart(2, '0')}
                                </div>
                                <div className="text-sm">
                                  {rackUnit ? (
                                    <span style={{ color: DesignSystem.colors.gray[900] }}>
                                      {rackUnit.device_model || 'Unknown Device'}
                                    </span>
                                  ) : (
                                    <span style={{ color: DesignSystem.colors.gray[500] }}>
                                      Empty slot
                                    </span>
                                  )}
                                </div>
                              </div>
                              {rackUnit && (
                                <div className="text-xs text-gray-500">
                                  {rackUnit.power_usage}W
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                          ) : (
                /* Front View: Carousel with single view */
              <>
                <div className="flex items-center justify-center space-x-8 transition-all duration-300 ease-in-out">
                  {/* Previous Rack */}
                  <div className="flex-shrink-0" style={{ transform: 'translateY(-50px)' }}>
                    {prevRack ? (
                      <div className="relative group">
                        <MiniRack 
                          rack={prevRack}
                          isBlurred={true}
                          onClick={() => navigateToRack(prevRack.id || '')}
                        />
                        <button
                          onClick={() => navigateToRack(prevRack.id || '')}
                          className="absolute -left-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-200 opacity-0 group-hover:opacity-100"
                          style={{ 
                            border: `1px solid ${DesignSystem.colors.border.gray200}`,
                            color: DesignSystem.colors.gray[600]
                          }}
                        >
                          <FaChevronLeft className="w-3 h-3" />
                        </button>
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          Previous
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg bg-gray-50" 
                        style={{ width: '280px', height: '500px' }}
                      >
                        <div className="text-gray-400 text-xs text-center">
                          <div>No previous</div>
                          <div>rack</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Current Rack */}
                  <div className="flex-shrink-0">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" style={{ width: '440px' }}>
                      <div className="flex items-center justify-between mb-6">
                        <h2 
                          className="text-lg font-semibold"
                          style={{ 
                            color: DesignSystem.colors.gray[900],
                            fontFamily: DesignSystem.typography.body.fontFamily
                          }}
                        >
                          {rack?.number || 'Rack'} Layout
                        </h2>
                        <div 
                          className="text-sm px-3 py-1 rounded-full"
                          style={{ 
                            backgroundColor: DesignSystem.colors.gray[100],
                            color: DesignSystem.colors.gray[700]
                          }}
                        >
                          Front View
                        </div>
                      </div>

                      <div className="rack-container">
                        <div className="bg-gray-900 rounded-t-lg p-4 mb-1">
                          <div className="flex items-center justify-between">
                            <div 
                              className="text-sm font-medium"
                              style={{ color: DesignSystem.colors.gray[300] }}
                            >
                              {rack?.number || 'Rack'} - Interactive View
                            </div>
                            <div className="flex space-x-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-800 rounded-b-lg p-2">
                          <div className="space-y-px">
                            {new Array(rack?.total_units || 45).fill(null).map((_, index) => {
                              const position = (rack?.total_units || 45) - index;
                              const rackUnit = rack?.units?.find((u) => u.position === position);
                              const { occupied } = getOccupiedPositions();
                              const isOccupiedByAbove = occupied.has(position);

                              return (
                                <RackUnit
                                  key={position}
                                  rackUnit={rackUnit}
                                  position={position}
                                  updateUnit={updateUnitHandler}
                                  deleteUnit={deleteUnitHandler}
                                  isOccupiedByAbove={isOccupiedByAbove}
                                  onDragStart={handleDragStart}
                                  onDragOver={handleDragOver}
                                  onDrop={handleDrop}
                                  onAddAsset={handleAddAsset}
                                  hasAsset={!!unitAssets[position]}
                                  assetName={unitAssets[position]?.assetName}
                                  onViewAsset={handleViewAsset}
                                  projectId={params.projectId}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Next Rack */}
                  <div className="flex-shrink-0" style={{ transform: 'translateY(-50px)' }}>
                    {nextRack ? (
                      <div className="relative group">
                        <MiniRack 
                          rack={nextRack}
                          isBlurred={true}
                          onClick={() => navigateToRack(nextRack.id || '')}
                        />
                        <button
                          onClick={() => navigateToRack(nextRack.id || '')}
                          className="absolute -right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-200 opacity-0 group-hover:opacity-100"
                          style={{ 
                            border: `1px solid ${DesignSystem.colors.border.gray200}`,
                            color: DesignSystem.colors.gray[600]
                          }}
                        >
                          <FaChevronRight className="w-3 h-3" />
                        </button>
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          Next
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg bg-gray-50" 
                        style={{ width: '280px', height: '500px' }}
                      >
                        <div className="text-gray-400 text-xs text-center">
                          <div>No next</div>
                          <div>rack</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rack Navigation Info */}
                {allRacks.length > 1 && (
                  <div className="mt-6 text-center">
                    <div 
                      className="text-sm"
                      style={{ 
                        color: DesignSystem.colors.gray[600],
                        fontFamily: DesignSystem.typography.small.fontFamily
                      }}
                    >
                      Showing rack {currentRackIndex + 1} of {allRacks.length} in this row
                    </div>
                    <div className="flex justify-center mt-2 space-x-2">
                      {allRacks.map((r, index) => (
                        <button
                          key={r.id}
                          onClick={() => navigateToRack(r.id || '')}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentRackIndex ? 'bg-blue-500' : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Instructions Panel */}
            <div 
              className="mt-6 p-4 rounded-lg border"
              style={{ 
                backgroundColor: DesignSystem.colors.brand[50],
                borderColor: DesignSystem.colors.brand[200]
              }}
            >
              <div className="flex items-start space-x-3">
                <FaInfoCircle 
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: DesignSystem.colors.brand[600] }}
                />
                <div>
                  <h3 
                    className="font-medium text-sm mb-2"
                    style={{ color: DesignSystem.colors.brand[900] }}
                  >
                    Rack Management
                  </h3>
                  <ul 
                    className="text-xs space-y-1"
                    style={{ color: DesignSystem.colors.brand[800] }}
                  >
                    <li>• Click empty slots to add devices</li>
                    <li>• Drag devices to move positions</li>
                    <li>• Click devices to edit properties</li>
                    <li>• Multi-unit devices span multiple slots</li>
                    {allRacks.length > 1 && (
                      <>
                        <li>• Click side racks or use arrows to navigate</li>
                        <li>• Use ← → keyboard arrows for quick navigation</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Fixed Color Legend - Bottom Right */}
      {showLegend && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 max-w-xs">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm text-gray-900">Legend</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsCleanView(!isCleanView)}
                  className={`flex items-center space-x-1 px-2 py-1 text-xs rounded transition-colors ${
                    isCleanView 
                      ? 'bg-blue-100 text-blue-700 border border-blue-300' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Toggle Clean View"
                >
                  <FaList className="w-3 h-3" />
                  <span>Clean</span>
                </button>
                <button 
                  onClick={() => setShowLegend(false)}
                  className="text-gray-400 hover:text-gray-600 text-xs hover:bg-gray-100 rounded px-1"
                >
                  ×
                </button>
              </div>
            </div>
          
          <div className="space-y-3 text-xs">
            {/* Device Status */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Device Status</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded border-l border-green-500"></div>
                  <span className="text-gray-600">1U Device</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded border-l border-amber-500"></div>
                  <span className="text-gray-600">Multi-unit Device</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gray-700 border border-gray-600 rounded"></div>
                  <span className="text-gray-600">Empty Slot</span>
                </div>
              </div>
            </div>

            {/* Cables & Ports */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Cables & Ports</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-1 bg-blue-600 rounded"></div>
                  <span className="text-gray-600">Network</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-1 bg-red-600 rounded"></div>
                  <span className="text-gray-600">Power</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-1 bg-yellow-600 rounded"></div>
                  <span className="text-gray-600">Extra Ports</span>
                </div>
              </div>
            </div>

            {/* LEDs */}
            <div>
              <div className="font-medium text-gray-700 mb-1">LED Status</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-gray-600">Power On</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-600">Network Active</span>
                </div>
              </div>
            </div>
                      </div>
          </div>
        </div>
      )}
      
      {/* Asset Modal */}
      <AddAssetToRackUnitModal
        isOpen={showAssetModal}
        onClose={() => {
          setShowAssetModal(false);
          setSelectedUnitPosition(null);
          setSelectedUnitId(null);
        }}
        projectId={params.projectId || ''}
        siteId={siteId}
        rackId={params.rackId}
        unitId={selectedUnitId}
        unitPosition={selectedUnitPosition || 0}
        totalRackUnits={rack?.total_units || 45}
        existingUnits={rack?.units?.map(u => ({ 
          position: u.position, 
          reserved_units: u.reserved_units || 1 
        })) || []}
        onAssetAdded={handleAssetAdded}
        onRackUnitCreated={() => forceReloadRooms(params.projectId || '', params.siteId || '', params.floorId || '')}
      />

      {/* Asset Details Modal */}
      {selectedAsset && (
        <AssetDetailsModal
          asset={selectedAsset}
          projectId={params.projectId || ''}
          isOpen={showAssetDetailsModal}
          onClose={() => {
            setShowAssetDetailsModal(false);
            setSelectedAsset(null);
          }}
        />
      )}
    </div>
  );
};
